import { useState } from 'react';
import DashboardRouteFallback from './DashboardRouteFallback';
import {
  getInitialSidebarCollapsed,
  getInitialSidebarWidth,
} from '../../utils/dashboardSidebarLayout';

function SkeletonBlock({ className = '' }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-paper-inset motion-reduce:animate-none ${className}`}
    />
  );
}

const labelClass =
  'h-[var(--cs-dashboard-skeleton-line-height)] w-[var(--cs-dashboard-skeleton-label-width)] max-w-full';
const iconClass =
  'h-[var(--cs-dashboard-shell-toolbar-height)] w-[var(--cs-dashboard-shell-toolbar-height)] shrink-0';

/** Also used while the real sidebar has mounted but the organization is loading. */
export function DashboardHeaderSkeleton() {
  return (
    <div
      data-dashboard-skeleton-section="header"
      aria-hidden="true"
      className="mb-[var(--cs-dashboard-shell-header-gap)]"
    >
      <div className="hidden h-[var(--cs-dashboard-shell-header-height)] items-center gap-[var(--cs-dashboard-shell-item-gap)] pr-[var(--cs-dashboard-shell-inset)] lg:flex">
        <SkeletonBlock className="h-[var(--cs-dashboard-shell-toolbar-height)] w-[var(--cs-dashboard-shell-toolbar-width)] shrink-0" />
        <SkeletonBlock className="h-[var(--cs-dashboard-shell-toolbar-height)] w-[var(--cs-dashboard-shell-tab-width)] max-w-full" />
      </div>
    </div>
  );
}

export default function DashboardShellSkeleton({ pathname, className = '' }) {
  const [width] = useState(getInitialSidebarWidth);
  const [collapsed] = useState(getInitialSidebarCollapsed);
  return (
    <div
      data-dashboard-shell-skeleton
      role="status"
      aria-label="Loading dashboard"
      aria-busy="true"
      className={`flex h-dvh min-h-0 flex-col overflow-hidden bg-paper-muted ${className}`}
    >
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          data-dashboard-skeleton-section="sidebar"
          aria-hidden="true"
          style={{ width }}
          className="hidden shrink-0 flex-col overflow-hidden px-[var(--cs-dashboard-shell-inset)] lg:flex"
        >
          <div className="mb-[var(--cs-dashboard-shell-section-gap)] mt-[var(--cs-dashboard-shell-header-gap)] flex items-center gap-[var(--cs-dashboard-shell-item-gap)]">
            <SkeletonBlock className={iconClass} />
            {!collapsed && <SkeletonBlock className={labelClass} />}
          </div>
          <SkeletonBlock className="mb-[var(--cs-dashboard-shell-section-gap)] h-[var(--cs-dashboard-shell-toolbar-height)] w-full" />
          {[4, 2, 3, 2].map((rows, section) => (
            <div key={section} className="mb-[var(--cs-dashboard-shell-section-gap)]">
              {section > 0 && !collapsed && (
                <SkeletonBlock
                  className={`${labelClass} mb-[var(--cs-dashboard-shell-item-gap)]`}
                />
              )}
              {Array.from({ length: rows }, (_, row) => (
                <div
                  key={row}
                  className="flex h-[var(--cs-dashboard-shell-toolbar-height)] items-center gap-[var(--cs-dashboard-shell-item-gap)]"
                >
                  <SkeletonBlock className="h-[var(--cs-dashboard-skeleton-line-height)] w-[var(--cs-dashboard-skeleton-line-height)] shrink-0" />
                  {!collapsed && <SkeletonBlock className={labelClass} />}
                </div>
              ))}
            </div>
          ))}
        </aside>
        <div
          data-dashboard-skeleton-section="content"
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
          <DashboardHeaderSkeleton />
          <DashboardRouteFallback pathname={pathname} />
        </div>
      </div>
      <div
        data-dashboard-skeleton-section="bottom-bar"
        aria-hidden="true"
        className="flex h-[var(--cs-dashboard-shell-bottom-height)] shrink-0 items-center justify-around gap-[var(--cs-dashboard-shell-item-gap)] px-[var(--cs-dashboard-shell-inset)] lg:justify-end"
      >
        {Array.from({ length: 3 }, (_, index) => (
          <SkeletonBlock key={index} className={labelClass} />
        ))}
      </div>
    </div>
  );
}
